import { Public, Meta } from '../lib/ui';
type Props = {
  className?: string;
};

const Content = () => (
  <>
    <p>
      These Terms govern your access to and use of Postdrop.io, as well as all
      content and Postdrop products and services available at or through these
      websites (collectively, “Services”). These Terms also govern visitors’
      access to and use of any websites that use our Services, such as emails
      hosted on Postdrop that are operated by our users. Our Services are
      offered subject to your acceptance, without modification, of all of the
      terms and conditions contained herein and all other operating rules,
      policies (including, without limitation, Postdrop’s Privacy Policy), and
      procedures that may be published from time to time by Postdrop
      (collectively, the “Agreement”). You agree that we may automatically
      upgrade our Services, and the Agreement will apply to any upgrades. Please
      read the Agreement carefully before accessing or using our Services. By
      accessing or using any part of our Services, you agree to become bound by
      the Agreement. If you do not agree to all the terms of the Agreement, then
      you may not access or use our Services.
    </p>
    <h3>1. Who’s Who</h3>
    <p>
      Throughout these Terms, “you” applies to both individuals and entities
      that access or use our Services. If you are an individual using our
      Services on behalf of an entity, you represent and warrant that you have
      the authority to bind that entity to the Agreement and that by using our
      Service(s), you are accepting the Agreement on behalf of that entity.
    </p>
    <h3>2. Your Account</h3>
    <p>
      Where use of our Services requires an account, you agree to provide us
      with complete and accurate information when you register for an account
      and keep the information current. This is important because sometimes we
      may need to send you notable updates (for example, about changes to our
      Terms of Service or Privacy Policy), or we may want to let you know about
      and make informed choices in response to legal inquiries or complaints.
    </p>
    <p>
      You will be solely responsible and liable for any activity that occurs
      under your account. You are responsible for keeping your account
      information up-to-date and for keeping your password secure.
    </p>
    <p>
      You are responsible for maintaining the security of your account and any
      Service-related website, store, or other content, and you are fully
      responsible for all activities that occur under your account and any other
      actions taken in connection with our Services. You shall not share or
      misuse your access credentials. You must immediately notify us of any
      unauthorized uses of your account, store, or website, or of any other
      breach of security. We will not be liable for any acts or omissions by
      you, including any damages of any kind incurred as a result of such acts
      or omissions.
    </p>
    <p>
      When you create a Postdrop account, we consider that to be an inquiry
      about our products and services, which means that we may contact you to
      share more details about what we have to offer. Don't worry -- if you
      aren't interested in learning more, you can opt out of the marketing
      communication, whether it's an email, phone call, or text message.
    </p>
    <h3>3. Minimum Age Requirements</h3>
    <p>
      Our Services are not directed to children. Access to and use of our
      Services is only for those over the age of 13 (or 16 in the European
      Union). If you are younger than this, you may not register for or use our
      Services. Any person who registers as a user or provides their personal
      information to our Services represents that they are 13 years of age or
      older (or 16 years or older in the European Union).
    </p>
    <h3>4. Responsibility of Visitors and Users</h3>
    <p>
      We have not reviewed, and cannot review, all of the content (such as, but
      not limited to, text, photo, video, audio, code, computer software, items
      for sale, or other materials) posted to our Services by users or anyone
      else (“Content”) and are not responsible for any use or effects of such
      Content. So, for example:
    </p>
    <p>
      We do not endorse any Content or represent that Content is accurate,
      useful, or non-harmful. Content could be offensive, indecent, or
      objectionable; include technical inaccuracies, typographical mistakes, or
      other errors; or violate or infringe the privacy, publicity rights,
      intellectual property rights (see our Copyright Infringement and DMCA
      Policy section to submit copyright complaints), or other proprietary
      rights of third parties.
    </p>
    <p>
      If you post Content or otherwise make (or allow any third party to make)
      Content available on our Services, you are entirely responsible for the
      Content, and any harm resulting from that Content or your conduct.
    </p>
    <p>
      We disclaim any responsibility for any harm resulting from anyone’s use,
      purchase, or downloading of Content. If you access or use any Content, you
      are responsible for taking precautions as necessary to protect yourself
      and your computer systems from viruses, worms, Trojan horses, and other
      harmful or destructive content.
    </p>
    <p>
      We are not a party to, and will have no responsibility or liability for,
      any communications, transactions, interactions, or disputes between you
      and the provider of any Content.
    </p>
    <p>
      Please note that additional third party terms and conditions may apply to
      the downloading, copying, purchase, or use of Content.
    </p>
    <p>
      We also have not reviewed, and cannot review, all of the material,
      including computer software, made available through the websites and web
      pages that link to, or are linked from, Postdrop or our other Services.
      For example:
    </p>
    <p>
      We do not have any control over those emails and are not responsible for
      their contents or their use.
    </p>
    <p>
      The existence of a link to or from one of our Services does not represent
      or imply that we endorse that website.
    </p>
    <p>
      You are responsible for taking precautions as necessary to protect
      yourself and your computer systems from viruses, worms, Trojan horses, and
      other harmful or destructive content.
    </p>
    <p>
      We disclaim any responsibility for any harm resulting from non-Postdrop
      websites.
    </p>
    <h3>5. Fees, Payment, and Renewal</h3>
    <p>a. Postdrop Fees</p>
    <p>
      Fees for Paid Services. Some of our Services are offered for a fee, such
      as Postdrop plans. By using a Paid Service, you agree to pay the specified
      fees and fees associated with specific features. Depending on the Paid
      Service, there may be one-time fees, recurring fees. For recurring fees,
      we’ll bill or charge you for in regular automatically-renewing intervals
      (such as monthly, annually, or biennially), on a pre-pay basis until you
      cancel, which you can do at any time by contacting the relevant support
      team.
    </p>
    <p>
      Taxes. To the extent permitted by law, or unless explicitly stated
      otherwise, all fees are exclusive of applicable federal, provincial,
      state, local or other governmental sales, goods and services, harmonized
      or other taxes, fees, or charges now in force or enacted in the future
      (“Taxes”). You are responsible for payment of all applicable Taxes
      relating to your use of our Services, your payments, or your purchases. If
      we are obligated to pay or collect Taxes on the Fees you’ve paid or will
      pay, you are responsible for such Taxes, and we may collect payment for
      such Taxes.
    </p>
    <p>
      Payment. If your payment fails or if Paid Services are otherwise not paid
      for or paid for on time, we may immediately cancel or revoke your access
      to the Paid Services. If you contact your bank or credit card company to
      decline or reverse the charge of fees for Paid Services, we may revoke
      your access to our Services in general.
    </p>
    <p>
      Automatic Renewal. To ensure uninterrupted service, recurring Paid
      Services are automatically renewed. This means that unless you cancel a
      Paid Service before the end of the applicable subscription period, it will
      automatically renew, and you authorize us to invoice you or use any
      payment mechanism we have on record for you to collect the then-applicable
      subscription fee (as well as any Taxes). By default, your Paid Services
      will be renewed for the same interval of time as your original
      subscription period. The date for the automatic renewal is determined
      automatically based on the date of the original purchase and cannot be
      changed.
    </p>
    <p>
      Cancelling Automatic Renewal. You can manage and cancel your Paid Services
      at the respective Service’s site. To cancel a Postdrop plan, visit your
      settings page and follow the instructions.
    </p>
    <p>
      Fee Changes. We may change our fees at any time, or start charging fees
      for Services that were previously free. When applicable, we may give you
      advance notice of the fee changes. If you don’t agree with the fee
      changes, you must cancel your Paid Service.
    </p>
    <p>
      Refunds. While you may cancel a Paid Service at any time, refunds are
      issued in our sole discretion, unless otherwise required by applicable
      law.
    </p>
    <h3>6. General Representation and Warranty</h3>
    <p>You represent and warrant that your use of our Services:</p>
    <p>Will be in strict accordance with these Terms;</p>
    <p>
      Will comply with all applicable laws and regulations (including, without
      limitation, all applicable laws regarding online conduct and acceptable
      content, privacy, data protection, the transmission of technical data
      exported from the United States or the country in which you reside, the
      use of financial services, notification and consumer protection, unfair
      competition, and false advertising);
    </p>
    <p>
      Will not use the Services for any unlawful purposes, to publish illegal
      content, or in furtherance of illegal activities;
    </p>
    <p>
      Will not infringe or misappropriate the intellectual property rights of
      any third party;
    </p>
    <p>
      Will not overburden Postdrop’s systems, as determined by us in our sole
      discretion;
    </p>
    <p>Will not disclose sensitive personal information of others;</p>
    <p>Will not be used to send spam or bulk unsolicited messages;</p>
    <p>
      Will not interfere with, disrupt, or attack any service or network; and
    </p>
    <p>
      Will not be used to create, distribute, or enable material that is - or
      that facilitates or operates in conjunction with - malware, spyware,
      adware, or other malicious programs or code.
    </p>
    <h3>7. Specific Service Terms</h3>
    <p>a. Postdrop Websites and Accounts</p>
    <p>
      Postdrop enables you to create and store email templates, and we would
      love for you to use it. A Postdrop account also allows you to sign into
      some of our Services. Postdrop’s basic service is free, and we offer paid
      plans for advanced features. Our service is designed to give you as much
      control and ownership over what goes on your email as possible and
      encourage you to express yourself freely. You own all content you post to
      your email. However, be responsible in what you publish. In particular,
      make sure that none of the prohibited items (like spam, viruses, or
      serious threats of violence) appear on your email.
    </p>
    <p>
      License. By submitting Content to Postdrop for inclusion on your email,
      you grant Postdrop a world-wide, royalty-free, and non-exclusive license
      to reproduce, modify, adapt, and publish the Content solely for the
      purpose of displaying, distributing, and promoting your website. This
      license also allows Postdrop to make any publicly-posted Content available
      to third parties selected by Postdrop so that these third parties can
      analyze and distribute (but not publicly display) the Content through
      their services. You also give other Postdrop users permission to share
      your Content on other Postdrop emails and add their own Content to it, so
      long as they use only a portion of your post and they give you credit as
      the original author by linking back to your email.
    </p>
    <p>
      Removing Content. If you delete Content, we will use reasonable efforts to
      remove it from public view (or in the case of a private website, from view
      by the authorized visitors) on Postdrop, but you acknowledge that caching
      or references to the Content may not be made immediately unavailable.
    </p>
    <h3>8. Copyright Infringement and DMCA Policy</h3>
    <p>
      As we ask others to respect our intellectual property rights, we respect
      the intellectual property rights of others. If you believe that material
      located on or associated with an Postdrop product or service violates your
      copyright, please notify us. We will respond to all such notices,
      including as required or appropriate by removing the infringing material
      or disabling all links to the infringing material. We will terminate a
      visitor’s access to and use of the website if, under appropriate
      circumstances, the visitor is determined to be a repeat infringer of the
      copyrights or other intellectual property rights of Postdrop or others. In
      the case of such termination, we will have no obligation to provide a
      refund of any amounts previously paid to us.
    </p>
    <h3>9. Intellectual Property</h3>
    <p>
      The Agreement does not transfer from Postdrop to you any Postdrop or third
      party intellectual property, and all right, title, and interest in and to
      such property will remain (as between the parties) solely with Postdrop.
      Postdrop, the Postdrop logo, and all other trademarks, service marks,
      graphics, and logos used in connection with Postdrop or our Services, are
      trademarks or registered trademarks of Postdrop or Postdrop’s licensors.
      Other trademarks, service marks, graphics, and logos used in connection
      with our Services may be the trademarks of other third parties. Your use
      of our Services grants you no right or license to reproduce or otherwise
      use any Postdrop or third party trademarks.
    </p>
    <h3>10. Third Party Services</h3>
    <p>
      In using the Services, you may enable or use services, products, software
      (like themes or plugins), embeds, or applications developed by a third
      party or yourself (“Third Party Services”) on your website. If you use any
      Third Party Services, you understand that:
    </p>
    <p>
      Third Party Services are not vetted, endorsed, or controlled by Postdrop.
    </p>
    <p>
      Any use of a Third Party Service is at your own risk, and we shall not be
      responsible or liable to anyone for Third Party Services.
    </p>
    <p>
      Your use is solely between you and the respective third party (“Third
      Party”) and will be governed by the Third Party’s terms and policies. It
      is your responsibility to review the Third Party’s terms and policies
      before using a Third Party Service.
    </p>
    <p>
      Some Third Party Services may request or require access to your (yours,
      your visitors’, or customers’) data, for example, via a pixel or cookie.
      If you use the Third Party Service or grant access, your data will be
      handled in accordance with the Third Party’s privacy policy and practices.
      We do not have control over how a Third Party Service may use your data.
      You should carefully review Third Party Services’ data collection,
      retention, and use policies and practices before enabling Third Party
      Services.
    </p>
    <p>
      Third Party Services may not work appropriately with your website, and we
      may not be able to provide support for issues caused by any Third Party
      Services.
    </p>
    <p>
      If you have questions or concerns about how a Third Party Service
      operates, or need support, please contact the Third Party directly.
    </p>
    <p>
      In rare cases, we may at our discretion, suspend, disable, or remove Third
      Party Services from your account or website.
    </p>
    <h3>11. Changes</h3>
    <p>
      We are constantly updating our Services and that means sometimes we have
      to change the legal terms under which our Services are offered. These
      Terms may only be modified by a written amendment signed by an authorized
      executive of Postdrop, or by the posting by Postdrop of a revised version.
      If we make changes that are material, we will let you know by posting on
      one of our blogs, or by sending you an email or other communication before
      the changes take effect. The notice will designate a reasonable period of
      time after which the new terms will take effect. If you disagree with our
      changes, then you should stop using our Services within the designated
      notice period, or once the changes become effective. Your continued use of
      our Services will be subject to the new terms. However, any dispute that
      arose before the changes shall be governed by the Terms (including the
      binding individual arbitration clause) that were in place when the dispute
      arose.
    </p>
    <h3>12. Termination</h3>
    <p>
      We may terminate your access to all or any part of our Services at any
      time, with or without cause, with or without notice, effective
      immediately. We have the right (though not the obligation) to, in our sole
      discretion, (i) reclaim your username or website’s URL due to prolonged
      inactivity, (ii) refuse or remove any content that, in our reasonable
      opinion, violates any Postdrop policy or is in any way harmful or
      objectionable, (iii) ask you to make some adjustments, restrict the
      resources your website uses, or terminate your Services, if we believe
      your website’s storage or bandwidth usage is out of hand and/or burdens
      our systems (which is rare and typically only occurs when a website is
      used for file sharing or storage), or (iv) terminate or deny access to and
      use of any of our Services to any individual or entity for any reason. We
      will have no obligation to provide a refund of any amounts previously
      paid.
    </p>
    <p>
      If you wish to terminate the Agreement or your Postdrop account, you may
      simply discontinue using our Services, or, if you are using a paid
      service, you may cancel at any time, subject to the Fees, Payment, and
      Renewal section in these Terms.
    </p>
    <p>
      All provisions of the Agreement which by their nature should survive
      termination shall survive termination, including, without limitation,
      ownership provisions, warranty disclaimers, indemnity, and limitations of
      liability.
    </p>
    <h3>13. Disclaimer of Warranties</h3>
    <p>
      Our Services are provided “as is.” Postdrop and its suppliers and
      licensors hereby disclaim all warranties of any kind, express or implied,
      including, without limitation, the warranties of merchantability, fitness
      for a particular purpose and non-infringement. Neither Postdrop, nor its
      suppliers and licensors, makes any warranty that our Services will be
      error free or that access thereto will be continuous or uninterrupted. If
      you’re actually reading this, here’s a treat. You understand that you
      download from, or otherwise obtain content or services through, our
      Services at your own discretion and risk.
    </p>
  </>
);

export default function SignUp(_props: Props) {
  return (
    <>
      <Meta title="Terms of Service" />
      <Public.Header title="Terms of Service" />
      <Public.Body width="max-w-4xl">
        <div className="prose prose-sm sm:prose md:prose-md lg:prose-lg">
          <Content />
        </div>
      </Public.Body>
    </>
  );
}

SignUp.Layout = Public;
